import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Chip, TableSortLabel, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import {
  calculateCoveredPercentage,
  calculateDebitForReceipt,
} from "../../../helpers/funcs/calculateDebitAndCPAndMismatchLogicForReceipt";
import { ReceiptStatus } from "../../../ReceiptsTable/ReceiptStatus";

interface Props {
  tableHeadings: { [key: string]: string };
  rows: any[];
}

const PairCurrencyMismatchTable = ({ tableHeadings, rows }: Props) => {
  const { linkedFmesByReceipt } = useSelector((state: any) => state.receipts);
  const headCells = Object.entries(tableHeadings);
  const tableRows = rows.map((receipt: any) => {
    const fmes = linkedFmesByReceipt[receipt.receipt_shared_uid] || [];

    const debit = calculateDebitForReceipt(fmes);
    const covered = calculateCoveredPercentage(debit, receipt.receipt_amount);

    return {
      company: receipt.finance_company_name,
      receipt_currency_symbol: receipt.receipt_currency_symbol,
      receipt_number: receipt.receipt_id,
      receipt_amount: receipt.receipt_amount,
      fme: fmes?.filter((f: any) => f && f.id != null).map((f: any) => f.id),
      debit,
      covered: covered ? `${covered}%` : "N/A",
      receipt_status: receipt.receipt_status,
    };
  });
  const formatAmount = (value: number | null | undefined): string => {
    if (typeof value !== "number" || Number.isNaN(value)) return "N/A";
    return value.toFixed(2);
  };

  return (
    <>
      <TableContainer className="receiptsTable " sx={{ my: 3 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map(([_, value], index) => (
                <TableCell key={index} align="left">
                  <TableSortLabel>
                    {value && typeof value === "string" ? value : ""}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows?.map((row) => (
              <React.Fragment key={row.receipt_number}>
                <TableRow
                  sx={{
                    "& > *": {
                      borderBottom:
                        "1px solid rgba(224, 224, 224, 1) !important",
                    },
                  }}
                >
                  {headCells.map(([key], cellIndex) => (
                    <TableCell key={cellIndex} align="left">
                      {key === "company" ? (
                        <>{row[key] ? row[key] : "N/A"}</>
                      ) : key === "receipt_number" ? (
                        <>{row[key] ? row[key] : "N/A"}</>
                      ) : key === "receipt_amount" ? (
                        <>
                          <Box display={"flex"} alignItems={"center"}>
                            {row[key] ? row[key] : "N/A"}{" "}
                            <Typography
                              sx={{
                                color: "rgba(0, 0, 0, 0.56)",
                                fontSize: "16px",
                                ml: 1,
                                fontWeight: 500,
                              }}
                            >
                              {row.receipt_currency_symbol ?? ""}
                            </Typography>
                          </Box>
                        </>
                      ) : key === "fme" ? (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {Array.isArray(row.fme) && row.fme.length > 0
                            ? row.fme.map((fmeId: string) => (
                                <Chip
                                  key={fmeId}
                                  label={fmeId}
                                  sx={{
                                    backgroundColor: "#ffffff",
                                    color: "rgba(0, 0, 0, 0.87)",
                                    borderRadius: "5px",
                                    boxShadow: "1px 1px 2px 1px #00000030",
                                    height: "26px",
                                    fontWeight: "500",
                                    cursor: "default",
                                    border: "1px solid #ffffff",
                                    "&:hover": {
                                      backgroundColor: "transparent",
                                      color: "#9c27b0",
                                      border: "1px solid #9c27b0",
                                    },
                                  }}
                                />
                              ))
                            : "N/A"}
                        </Box>
                      ) : key === "debit" ? (
                        <>
                          <Box display={"flex"} alignItems={"center"}>
                            {formatAmount(row[key]) ?? "N/A"}{" "}
                            <Typography
                              sx={{
                                color: "rgba(0, 0, 0, 0.56)",
                                fontSize: "16px",
                                ml: 1,
                                fontWeight: 500,
                              }}
                            >
                              {row.receipt_currency_symbol ?? ""}
                            </Typography>
                          </Box>
                        </>
                      ) : key === "covered" ? (
                        <>{row[key] ? row[key] : "N/A"}</>
                      ) : key === "fme_status" ? (
                        <>
                          <Box display={"flex"} alignItems={"center"}>
                            <div className="AlertItems">
                              <ReceiptStatus status={row?.receipt_status} />
                            </div>
                          </Box>
                        </>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default PairCurrencyMismatchTable;
